/**
 * Dependencies.
 */
import Moment from 'moment-timezone';
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';

import DriverModel from '@/api/driver/driver.model';
import TrackingModel from '@/api/tracking/tracking.model';
import VehicleModel from '@/api/vehicle/vehicle.model';
import DeliveredSP from '@/modules/tracking/components/status/delivered-sp/delivered-service-point.component.vue';
import StatusFakeP from '@/modules/tracking/components/status/fake-processing/status-fake-processing.component.vue';
// eslint-disable-next-line max-len
import StatusInInterruptedRoute from '@/modules/tracking/components/status/in-interrupted-route/in-interrupted-route.component.vue';
import StatusInRouting from '@/modules/tracking/components/status/in-routing/status-in-routing.component.vue';
import StatusInSP from '@/modules/tracking/components/status/in-service-point/status-in-service-point.component.vue';
import StatusWaySP from '@/modules/tracking/components/status/on-the-way-to-sp/status-on-the-way-to-sp.component.vue';
import StatusProcessing from '@/modules/tracking/components/status/processing/status-processing.component.vue';
import StatusSuccess from '@/modules/tracking/components/status/success/status-success.component.vue';
import ToCollect from '@/modules/tracking/components/status/to-collect-in-sp/status-to-collect-in-sp.component.vue';
import StatusUnsuccess from '@/modules/tracking/components/status/unsuccess/status-unsuccess.component.vue';
import { TRACKING_STATUS as TrackingStatus } from '@/modules/tracking/constants';

/**
 * Component.
 */
@Component({
  components: {
    StatusFakeProcessing: StatusFakeP,
    StatusInInterruptedRoute,
    StatusInRouting,
    StatusInServicePoint: StatusInSP,
    StatusOnTheWayToServicePoint: StatusWaySP,
    StatusProcessing,
    StatusSuccess,
    StatusToCollectInServicePoint: ToCollect,
    StatusUnsuccess,
    'delivered-service-point': DeliveredSP
  },
  name: 'status-card-component'
})
export default class extends Vue {
  /**
   * Property `moment`.
   */
  moment = Moment;

  /**
   * Tracking status.
   */
  trackingStatus = TrackingStatus;

  /**
   * Property `betweenFromDate`.
   */
  @Prop({ type: String })
  betweenFromDate: string;

  /**
   * Property `betweenToDate`.
   */
  @Prop({ type: String })
  betweenToDate: string;

  /**
   * Property `client`.
   */
  @Prop({ type: String })
  client: string;

  /**
   * Property `driver`.
   */
  @Prop({ type: Object })
  driver: DriverModel;

  /**
   * Property `distributionCenter`.
   */
  @Prop({ type: String })
  distributionCenter: string;

  /**
   * Property `tracking`.
   */
  @Prop({ type: Object })
  tracking: TrackingModel;

  /**
   * Property `vehicle`.
   */
  @Prop({ type: Object })
  vehicle: VehicleModel;

  /**
   * Property `supportUrl`.
   */
  @Prop()
  supportUrl: string;

  /**
   * Property `fakeProcessingDate`.
   */
  @Prop({ default: null })
  fakeProcessingDate: string;

  /**
   * Property `servicePoint`.
   */
  @Prop({ default: '', type: String })
  servicePoint: string;

  /**
   * Property `isScanningQrcode`.
   */
  @Prop({ default: false, type: Boolean })
  isScanningQrcode: boolean;

  /**
   * Property `isOnTheWayToServicePoint`.
   */
  @Prop({ default: false, type: Boolean })
  isOnTheWayToServicePoint: boolean;

  /**
   * Property `orderType`.
   */
  @Prop({ default: null, type: String })
  orderType: string;

  /**
   * Property `isToCollectInServicePoint`.
   */
  @Prop({ default: false, type: Boolean })
  isToCollectInServicePoint: boolean;

  /**
   * Property `expectedDeliveryDate`.
   */
  @Prop({ default: null, type: String })
  expectedDeliveryDate: boolean;

  /**
   * Property `servicePointRetentionTime`.
   */
  @Prop({ default: null, type: Number })
  servicePointRetentionTime: number;

  /**
   * Property `numberOfPackages`.
   */
  @Prop({ default: 0, type: Number })
  numberOfPackages: number;

  /**
   * Property `isOrderProcessDelayed`.
   */
  @Prop({ default: false, type: Boolean })
  isOrderProcessDelayed: boolean;

  /**
   * Getter is status processing.
   */
  get isStatusProcessing() {
    const { PROCESSING } = this.trackingStatus;
    const { status: { code = null } = {} } = this.tracking || {};

    return code === PROCESSING && !this.fakeProcessingDate;
  }

  /**
   * Getter is status in routing.
   */
  get isStatusInRouting() {
    const { IN_ROUTING } = this.trackingStatus;
    const { status: { code = null } = {} } = this.tracking || {};

    return code === IN_ROUTING;
  }

  /**
   * Getter is status in interruped route.
   */
  get isStatusInInterruptedRoute() {
    const { IN_INTERRUPTED_ROUTE } = this.trackingStatus;
    const { status: { code = null } = {} } = this.tracking || {};

    return code === IN_INTERRUPTED_ROUTE;
  }

  /**
   * Getter is status in service point.
   */
  get isStatusInServicePoint() {
    const { IN_SERVICE_POINT } = this.trackingStatus;
    const { status: { code = null } = {} } = this.tracking || {};

    return code === IN_SERVICE_POINT;
  }

  /**
   * Getter is status success.
   */
  get isStatusSuccess() {
    const { COLLECTED, DELIVERED } = this.trackingStatus;
    const { status: { code = null } = {} } = this.tracking || {};

    return code === DELIVERED || code === COLLECTED;
  }

  /**
   * Getter is status unsuccess.
   */
  get isStatusUnsuccess() {
    const {
      CANCELED,
      IN_TREATMENT,
      NOT_COLLECTED,
      NOT_DELIVERED,
      RETURNED
    } = this.trackingStatus;
    const { status: { code = null } = {} } = this.tracking || {};

    return (
      code === NOT_COLLECTED ||
      code === NOT_DELIVERED ||
      code === IN_TREATMENT ||
      code === RETURNED ||
      code === CANCELED
    );
  }

  /**
   * Getter is delivered to service point.
   */
  get isDeliveredToServicePoint() {
    const { isStatusSuccess, servicePoint } = this || {};
    const isInServicePoint = !!servicePoint;

    return isInServicePoint && isStatusSuccess;
  }
}
